import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import Select, {
	ActionMeta,
	components as rsComponents,
	GroupBase,
	InputActionMeta,
	MenuProps,
	Props as ReactSelectProps,
	SingleValue,
} from "react-select";
import {
	DropdownIndicator,
	Option,
} from "app/pages/.shared/form/AdvancedSelectFormik/componentsOverrides";
import "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik.scss";
import { HotelSearchOption, SearchOption } from "../../types";
import LocationMenuList from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuList";
import LocationMenuChip from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuChip";
import InputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/InputValueFormat";
import ClearIndicator from "app/pages/.shared/form/AdvancedSelectFormik/componentsOverrides/ClearIndicator";
import TopOptionChip from "app/pages/SmartDP/Search/SDPSearchForm/TopOptionChip/TopOptionChip";
import { ICON_SIZES, SEARCH_FUZZY_THRESHOLD } from "app/constants";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import ButtonGroup from "app/components/ui/buttonGroup/ButtonGroup";
import { useIntl } from "react-intl";
import "./LocationCombobox.scss";
import AppGlobalsContext from "app/AppGlobalsContext";
import { useDebounce, useDebouncedFuzzySearch, useSearchHotels } from "app/hooks";
import { sortOptions as sortDestinations } from "app/utils/utils";
import { renderDestinationOption } from "app/features/SearchEngine/searchJourney/components/locationCombobox/LocationCombobox";
import { HotelStrokeIcon } from "app/icons/travel";
import { smartDPDestinationSuggestion } from "app/utils/smartDPUtils";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

type FilterValue = "all" | "destinations" | "hotels";

type DestinationComboboxChangeMeta = {
	showChipMenu: boolean;
	actionMeta: ActionMeta<SearchOption>;
};

export type DestinationComboboxProps = {
	id: string;
	label: ReactNode;
	menuListMaxHeight?: number | string;
	options: SearchOption[];
	optionsWhenEmpty?: SearchOption[];
	onFocus?: () => void;
	onChange?: (option: SingleValue<SearchOption>, meta: DestinationComboboxChangeMeta) => void;
} & Omit<
	ReactSelectProps<SearchOption, false, GroupBase<SearchOption>>,
	"options" | "onChange" | "onInputChange" | "inputValue"
>;

const getOptionLabelText = (option: SearchOption): string => {
	if (typeof option === "string") {
		return option;
	}
	if (option && typeof option === "object" && "label" in option) {
		return String(option.label);
	}
	return "";
};

const isHotelOption = (option: SearchOption): boolean =>
	typeof option === "object" &&
	option !== null &&
	Boolean((option as Record<string, unknown>).isHotel);

const renderHotelOption = (option: SearchOption) => {
	const name = getOptionLabelText(option);
	const { places } = (option as unknown) as HotelSearchOption;
	const location = places ? places.map(place => place.displayName).join(", ") : undefined;

	return (
		<div className="search-form__suggestion">
			<div className="search-form__suggestion-picto">
				<HotelStrokeIcon height={ICON_SIZES.LG} width={ICON_SIZES.LG} />
			</div>
			<div className="search-form__suggestion-text">
				<Typography
					className="search-form__suggestion-first-label"
					variant={TYPOGRAPHY_VARIANTS.REGULAR}
				>
					{name}
				</Typography>
				{location && (
					<span className="search-form__suggestion-second-label">{location}</span>
				)}
			</div>
		</div>
	);
};

const toHotelSearchOption = (hotel: HotelSearchOption): SearchOption => ({
	code: hotel.id,
	label: hotel.name,
	isHotel: true,
	places: hotel.places,
});

// Defined outside the component so react-select does not remount it on re-renders.
const DestinationMenu = ({
	selectProps,
	children,
	...props
}: MenuProps<SearchOption, false, GroupBase<SearchOption>>) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const { filterGroupOptions, activeFilter, onFilterChange } = selectProps as any;

	return (
		<rsComponents.Menu className="advanced-select-menu" selectProps={selectProps} {...props}>
			{filterGroupOptions && (
				<div className="location-combobox__filters" onMouseDown={e => e.preventDefault()}>
					<ButtonGroup
						options={filterGroupOptions}
						value={activeFilter}
						onChange={onFilterChange}
					/>
				</div>
			)}
			{children}
		</rsComponents.Menu>
	);
};

export const DestinationCombobox = ({
	id,
	value,
	options,
	optionsWhenEmpty,
	menuListMaxHeight = 485,
	classNamePrefix = "advanced-select__select",
	label,
	placeholder: placeholderProp = "",
	isClearable = true,
	onChange,
	onFocus,
	...rest
}: DestinationComboboxProps) => {
	const { formatMessage } = useIntl();
	const { partnerCode } = useContext(AppGlobalsContext);

	const { track } = useAnalytics();

	const [inputValue, setInputValue] = useState("");
	const [isFocused, setIsFocused] = useState(false);
	const [activeFilter, setActiveFilter] = useState<FilterValue>("all");

	const debouncedInputValue = useDebounce(inputValue, 300);
	const { data: hotelSearchData } = useSearchHotels({
		q: debouncedInputValue,
		partnerCode,
	});

	const hotelOptions: HotelSearchOption[] = useMemo(() => {
		const results: HotelSearchOption[] = (hotelSearchData && hotelSearchData.results) || [];

		track("search_query_input", {
			rawQuery: debouncedInputValue,
			results: hotelSearchData && hotelSearchData.results,
		});

		return results
			.sort(
				(
					a: HotelSearchOption & { score?: number },
					b: HotelSearchOption & { score?: number }
				) => (b.score || 0) - (a.score || 0)
			)
			.slice(0, 20);
	}, [hotelSearchData]);

	const fuzzyOptions = useDebouncedFuzzySearch(options, inputValue, {
		// @ts-expect-error TODO: corriger cette erreur, keys n'est pas défini dans les options de useDebouncedFuzzySearch
		keys: ["labels"],
		fuseOptions: { threshold: SEARCH_FUZZY_THRESHOLD },
	});

	const filteredOptions = useMemo(() => sortDestinations(options, fuzzyOptions, inputValue), [
		options,
		fuzzyOptions,
		inputValue,
	]);

	const optionsWhenEmptyCount = optionsWhenEmpty === undefined ? 0 : optionsWhenEmpty.length;
	const showChipMenu = inputValue.trim().length === 0 && optionsWhenEmptyCount > 0;
	const showFilterGroup = !showChipMenu;

	useEffect(() => {
		if (showChipMenu) {
			setActiveFilter("all");
		}
	}, [showChipMenu]);

	const hotelSearchOptions: SearchOption[] = useMemo(
		() => (hotelOptions || []).map(toHotelSearchOption),
		[hotelOptions]
	);

	const chipMenuOptions: SearchOption[] = optionsWhenEmpty || [];
	const destinationBaseOptions: SearchOption[] = showChipMenu ? chipMenuOptions : filteredOptions;

	const computedOptions: SearchOption[] = useMemo(() => {
		if (!showFilterGroup) {
			return destinationBaseOptions;
		}
		if (activeFilter === "destinations") {
			return destinationBaseOptions;
		}
		if (activeFilter === "hotels") {
			return hotelSearchOptions;
		}
		return [...destinationBaseOptions, ...hotelSearchOptions];
	}, [showFilterGroup, activeFilter, destinationBaseOptions, hotelSearchOptions]);

	const computedMenuList = showChipMenu ? LocationMenuChip : LocationMenuList;

	const filterGroupOptions = showFilterGroup
		? [
				{
					value: "all",
					label: formatMessage({ id: "sdp.search.filter.all" }),
				},
				{
					value: "destinations",
					label: formatMessage({ id: "sdp.search.filter.destinations" }),
				},
				{
					value: "hotels",
					label: formatMessage({ id: "sdp.search.filter.hotels" }),
				},
		  ]
		: undefined;

	const isFloating = isFocused || Boolean(value) || inputValue.trim().length > 0;
	const placeholder = isFloating ? placeholderProp : "";

	const formatOptionLabel = (option: SearchOption) => {
		if (showChipMenu && typeof option === "object") {
			const firstSuggestion = option.labels ? option.labels.slice(-1).join(", ") : "";
			const secondSuggestion = smartDPDestinationSuggestion(firstSuggestion, option.levels);
			return (
				<TopOptionChip
					label={[firstSuggestion, secondSuggestion].filter(Boolean).join(", ")}
				/>
			);
		}

		return isHotelOption(option) ? renderHotelOption(option) : renderDestinationOption(option);
	};

	const handleInputChange = useCallback((newVal: string, { action }: InputActionMeta) => {
		if (action === "input-blur" || action === "menu-close") {
			return;
		}
		setInputValue(newVal);
	}, []);

	const getOptionValue = useCallback(
		(option: SearchOption) =>
			typeof option === "object" ? String(option.code) : String(option),
		[]
	);

	const getOptionLabel = useCallback(
		(option: SearchOption) =>
			typeof option === "object" ? String(option.label) : String(option),
		[]
	);

	const handleChange = useCallback(
		(option: SingleValue<SearchOption>, actionMeta: ActionMeta<SearchOption>) => {
			if (onChange) {
				onChange(option, { showChipMenu, actionMeta });
			}
		},
		[onChange, showChipMenu]
	);

	return (
		<div className="location-combobox advanced-select">
			<Select
				styles={{
					menuList: (base = {}) => ({
						...base,
						maxHeight: menuListMaxHeight,
					}),
				}}
				value={value}
				inputValue={inputValue}
				options={computedOptions}
				onInputChange={handleInputChange}
				isClearable={isClearable}
				components={{
					Menu: DestinationMenu,
					Option,
					DropdownIndicator,
					ClearIndicator,
					SingleValue: InputValueFormat,
					// @ts-expect-error TODO: fix this error, LocationMenuChip is not compatible with MenuListProps
					MenuList: computedMenuList,
				}}
				classNamePrefix={classNamePrefix}
				menuIsOpen={true}
				getOptionValue={getOptionValue}
				getOptionLabel={getOptionLabel}
				onFocus={() => {
					if (typeof onFocus === "function") {
						onFocus();
					}
					setIsFocused(true);
				}}
				onBlur={() => setIsFocused(false)}
				inputId={id}
				maxMenuHeight={485}
				placeholder={placeholder}
				formatOptionLabel={formatOptionLabel}
				onChange={handleChange}
				filterOption={() => true}
				{...showFilterGroup && {
					activeFilter,
					onFilterChange: setActiveFilter,
					filterGroupOptions,
				}}
				{...showChipMenu && {
					locationMenuLabel: formatMessage({
						id: "sdp.search.top.destinations.cities.label",
					}),
				}}
				{...rest}
			/>

			<label
				className={classNames("location-combobox__label", {
					"location-combobox__label--floating": isFloating,
				})}
				htmlFor={id}
			>
				{isFloating ? (
					<Typography variant={TYPOGRAPHY_VARIANTS.XSMALL}>{label}</Typography>
				) : (
					<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>{label}</Typography>
				)}
			</label>
		</div>
	);
};
